import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { sanitizeHtml } from "lib/htmlIsland/sanitizeHtml";
import { buildScopeTranslate } from "lib/I18n";
import {
  choiceLetter,
  getResponseIconKey,
  getSaqBandLabel,
  getSaqFeedbackState,
  getSaqFeedbackTone,
  iconAltText,
} from "../resultUtils";
import { ATTEMPT_STATUS, QUESTION_TYPES } from "../constants";
import success from "images/assessment-framework/success.svg";
import error from "images/assessment-framework/error.svg";
import loading from "images/assessment-framework/loading.svg";
import warning from "images/assessment-framework/warning.svg";

const tResults = buildScopeTranslate(
  "views.content.assessment_framework.assessments.pages.results"
);

const RESPONSE_ICONS = {
  success: { src: success, altKey: "icon_correct" },
  error: { src: error, altKey: "icon_incorrect" },
  warning: { src: warning },
  loading: { src: loading, altKey: "icon_loading" },
};

function selectedChoices(response) {
  const selectedIds = response.learner_response?.selected_choice_ids || [];

  return (response.choices || []).flatMap((choice, index) => (
    selectedIds.includes(choice.id)
      ? [{ id: choice.id, letter: choiceLetter(index), text: choice.text }]
      : []
  ));
}

// The icon beside the question. It is a plain placeholder when the result is not known, so the
// text stays aligned with the cards that have one.
const Badge = ({ response, status }) => {
  const iconKey = getResponseIconKey(response, status);
  const icon = RESPONSE_ICONS[iconKey];
  const className = classNames("th-agentforce-assessment__question-result-card-badge", {
    "th-agentforce-assessment__question-result-card-badge--loading": iconKey === "loading",
  });

  if (!icon) {
    return <span className={className} aria-hidden="true" />;
  }

  return <img className={className} src={icon.src} alt={iconAltText(response, status, iconKey, icon, tResults)} />;
};

const SelectedAnswer = ({ response }) => {
  const choices = selectedChoices(response);

  return (
    <div className="th-agentforce-assessment__question-result-answer">
      <h4 className="th-agentforce-assessment__question-result-section-title">
        {tResults(choices.length > 1 ? "your_answers" : "your_answer")}
      </h4>
      {choices.map((choice) => (
        <div key={choice.id} className="th-agentforce-assessment__question-result-selected-choice">
          <p className="th-agentforce-assessment__question-result-selected-letter">{choice.letter}</p>
          <p>{choice.text}</p>
        </div>
      ))}
    </div>
  );
};

const SaqFeedback = ({ response, status }) => {
  const { html, shaded, muted, busy } = getSaqFeedbackState(response, status, tResults);
  const bandLabel = getSaqBandLabel(response, status, tResults);
  const tone = getSaqFeedbackTone(response, status);
  const responseText = response.learner_response?.text
    || (status === ATTEMPT_STATUS.ERROR ? tResults("response_pending") : undefined);

  return (
    <div className="th-agentforce-assessment__question-result-feedback">
      <div className="th-agentforce-assessment__question-result-response-container">
        <h4 className="th-agentforce-assessment__question-result-section-title">
          {tResults("your_response")}
        </h4>
        <p className="th-agentforce-assessment__question-result-response-text">{responseText}</p>
      </div>
      <div
        className={classNames(
          "th-agentforce-assessment__question-result-feedback-container",
          { "th-agentforce-assessment__question-result-feedback-container-grading": shaded },
          tone && `th-agentforce-assessment__question-result-feedback-container--${tone}`
        )}
        aria-live="polite"
        aria-busy={busy}>
        <h4
          className="th-agentforce-assessment__question-result-section-title"
          aria-label={bandLabel ? tResults("feedback_with_result", { grade: bandLabel }) : undefined}>
          {bandLabel ? tResults("feedback_with_category", { grade: bandLabel }) : tResults("feedback")}
        </h4>
        <div
          className={muted ? "th-agentforce-assessment__question-result-evaluating" : undefined}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
        />
      </div>
    </div>
  );
};

const QuestionResultCard = ({ response, status }) => {
  if (!response?.type) {
    return null;
  }

  const questionType = response.type.toUpperCase();

  return (
    <li className="th-agentforce-assessment__question-result-card">
      <Badge response={response} status={status} />
      <div className="th-agentforce-assessment__question-result-card-content">
        <div className="th-agentforce-assessment__question-result-header">
          <h3 className="th-agentforce-assessment__question-result-question-label">
            {response.prompt}
          </h3>
        </div>
        {questionType === QUESTION_TYPES.MCQ && <SelectedAnswer response={response} />}
        {questionType === QUESTION_TYPES.SAQ && <SaqFeedback response={response} status={status} />}
      </div>
    </li>
  );
};

const responseShape = PropTypes.shape({
  prompt: PropTypes.string,
  text: PropTypes.string,
  saq_grade: PropTypes.shape({
    band: PropTypes.string,
    feedback: PropTypes.string,
  }),
}).isRequired;

Badge.propTypes = {
  response: responseShape,
  status: PropTypes.string,
};

SelectedAnswer.propTypes = { response: responseShape };

SaqFeedback.propTypes = { response: responseShape, status: PropTypes.string };

QuestionResultCard.propTypes = {
  response: responseShape,
  status: PropTypes.string,
};

export default QuestionResultCard;
